import { environment } from "../../config/environment";
import { storage } from "../../utils/storage";

export class BaseApiService {
  protected baseUrl: string;
  protected authUrl: string;
  
  constructor() {
    this.baseUrl = environment.API_URL;
    this.authUrl = environment.AUTH_URL;
  }

  protected async fetchWithAuth(endpoint: string, options: RequestInit = {}): Promise<any> {
    try {
      const accessToken = storage.getAccessToken();
      
      if (accessToken) {
        options.headers = {
          ...options.headers,
          'Authorization': `Bearer ${accessToken}`
        };
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, options);

      // Handle 401 Unauthorized
      if (response.status === 401) {
        const refreshed = await this.handleTokenRefresh();
        if (refreshed) {
          // Retry the original request with new token
          return this.fetchWithAuth(endpoint, options);
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  private async handleTokenRefresh(): Promise<boolean> {
    try {
      const refreshToken = storage.getRefreshToken();
      
      if (!refreshToken) {
        storage.clearAuthData();
        this.handleAuthFailure();
        return false;
      }

      const requestBody = {
        grant_type: 'refresh_token',
        client_id: environment.CLIENT_ID,
        refresh_token: refreshToken,
      };

      const refreshTokenUrl = `${environment.LOGIN_URL}/refreshtoken`;
      
      const response = await fetch(refreshTokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        storage.clearAuthData();
        this.handleAuthFailure();
        return false;
      }

      const data = await response.json();
      const currentAuthData = storage.getAuthData();
      storage.setAuthData({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        user: currentAuthData?.user || {
          id: 0,
          phone_no: '',
          is_verified: false,
          is_patient: false
        },
        session: currentAuthData?.session || {
          id: 0,
          status: '',
          start_time: new Date().toISOString()
        }
      });

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      storage.clearAuthData();
      this.handleAuthFailure();
      return false;
    }
  }

  private handleAuthFailure(): void {
    // Simply redirect to login page
    console.log('🔐 Auth failure detected - redirecting to login...');
    if (typeof window !== 'undefined') {
      window.location.href = '/patient/login';
    }
  }

  protected encrypt(data: any): string {
    // Add your encryption logic here
    // This is just a placeholder
    return JSON.stringify(data);
  }

  protected decrypt(data: string): any {
    // Add your decryption logic here
    // This is just a placeholder
    return JSON.parse(data);
  }
} 